import { useEffect, useMemo, useRef, useState } from 'react';
import { Star, ChevronDown, Check, Plus } from 'lucide-react';
import {
  useWatchlists,
  useEntityWatchlists,
  useAddEntityToWatchlist,
  useRemoveEntityFromWatchlist,
  useCreateWatchlist,
} from '../api/hooks.js';

// Multi-watchlist "Add to watchlist" button + dropdown. The star reflects
// membership in ANY watchlist; the dropdown lists every watchlist with a check
// for the ones this entity is on, toggling add/remove per row, plus inline
// create-new. Shared by the Company Overview header and the map popups (Map +
// Bolt-On panels), so the membership model is identical everywhere.

export function ShortlistButton({ entityId }: { entityId: string }) {
  const [open, setOpen] = useState(false);
  const [naming, setNaming] = useState(false);
  const [draftName, setDraftName] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);

  const { data: watchlistsData } = useWatchlists();
  const { data: entityWatchlistsData } = useEntityWatchlists(entityId);
  const addMut = useAddEntityToWatchlist();
  const removeMut = useRemoveEntityFromWatchlist();
  const createMut = useCreateWatchlist();

  const watchlists = watchlistsData?.data ?? [];
  // Server truth for membership, overlaid with an optimistic override so a
  // tick/untick flips instantly (the Supabase refetch lags ~0.5s). The override
  // clears once the server catches up, and resets when the entity changes.
  const serverSet = useMemo(
    () => new Set((entityWatchlistsData?.data ?? []).map((w) => w.watchlistId)),
    [entityWatchlistsData],
  );
  const [override, setOverride] = useState<Record<string, boolean>>({});
  useEffect(() => setOverride({}), [entityId]);
  useEffect(() => {
    setOverride((o) => {
      let changed = false;
      const next = { ...o };
      for (const id of Object.keys(next)) {
        if (serverSet.has(id) === next[id]) {
          delete next[id];
          changed = true;
        }
      }
      return changed ? next : o;
    });
  }, [serverSet]);
  const effectiveMembers = useMemo(() => {
    const s = new Set(serverSet);
    for (const [id, member] of Object.entries(override)) {
      if (member) s.add(id);
      else s.delete(id);
    }
    return s;
  }, [serverSet, override]);
  const isMember = (id: string) => effectiveMembers.has(id);
  const isShortlisted = effectiveMembers.size > 0;

  useEffect(() => {
    if (!open) return undefined;
    function onDown(e: MouseEvent) {
      if (!rootRef.current?.contains(e.target as Node)) {
        setOpen(false);
        setNaming(false);
      }
    }
    function onEsc(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setOpen(false);
        setNaming(false);
      }
    }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  const toggle = (watchlistId: string) => {
    const member = isMember(watchlistId);
    setOverride((o) => ({ ...o, [watchlistId]: !member }));
    const revert = () =>
      setOverride((o) => {
        const next = { ...o };
        delete next[watchlistId];
        return next;
      });
    if (member) removeMut.mutate({ watchlistId, entityId }, { onError: revert });
    else addMut.mutate({ watchlistId, entityId }, { onError: revert });
  };
  const commitNew = () => {
    const name = draftName.trim();
    if (!name) return;
    createMut.mutate(name, {
      onSuccess: ({ data }) => {
        addMut.mutate({ watchlistId: data.id, entityId });
        setOverride((o) => ({ ...o, [data.id]: true }));
        setNaming(false);
        setDraftName('');
      },
    });
  };

  return (
    <div ref={rootRef} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        title={isShortlisted ? 'On a ward — manage' : 'Add to ward'}
        style={actBtnStyle(isShortlisted)}
      >
        <Star size={11} fill={isShortlisted ? 'currentColor' : 'none'} />
        {isShortlisted ? 'Ward' : 'Add to ward'}
        <ChevronDown size={10} />
      </button>
      {open && (
        <div
          role="menu"
          style={{
            position: 'absolute',
            top: '100%',
            right: 0,
            marginTop: 4,
            minWidth: 200,
            maxWidth: 280,
            background: 'var(--menu-surface)',
            border: '1px solid var(--hairline)',
            borderRadius: 3,
            boxShadow: '0 6px 24px rgba(10,14,26,0.14), 0 2px 6px rgba(10,14,26,0.08)',
            zIndex: 9000,
            padding: 4,
            fontSize: 'var(--t-body)',
          }}
        >
          {watchlists.length === 0 && (
            <div
              style={{
                padding: '4px 6px',
                color: 'var(--text-tertiary)',
                fontSize: 'var(--t-meta)',
              }}
            >
              No wards yet.
            </div>
          )}
          {watchlists.map((w) => (
            <button
              key={w.id}
              type="button"
              role="menuitemcheckbox"
              aria-checked={isMember(w.id)}
              onClick={() => toggle(w.id)}
              style={menuItemStyle()}
              onMouseOver={(e) => (e.currentTarget.style.background = 'var(--hover-subtle)')}
              onMouseOut={(e) => (e.currentTarget.style.background = 'transparent')}
            >
              {isMember(w.id) ? (
                <Check size={12} color="var(--accent)" />
              ) : (
                <span style={{ width: 12 }} />
              )}
              <span
                style={{
                  flex: 1,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {w.name}
              </span>
              <span
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 'var(--t-meta)',
                  color: 'var(--text-tertiary)',
                }}
              >
                {w.entityCount.toLocaleString('en-GB')}
              </span>
            </button>
          ))}
          <div style={{ borderTop: '1px solid var(--hairline)', marginTop: 4, paddingTop: 4 }}>
            {naming ? (
              <div style={{ display: 'flex', gap: 4, padding: '2px 4px' }}>
                <input
                  autoFocus
                  value={draftName}
                  onChange={(e) => setDraftName(e.target.value)}
                  placeholder="New ward…"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      commitNew();
                    } else if (e.key === 'Escape') {
                      e.preventDefault();
                      setNaming(false);
                    }
                  }}
                  style={{
                    flex: 1,
                    padding: '2px 4px',
                    border: '1px solid var(--accent)',
                    borderRadius: 2,
                    background: 'var(--surface)',
                    color: 'var(--text-primary)',
                    fontSize: 'var(--t-body)',
                  }}
                />
                <button
                  type="button"
                  onClick={commitNew}
                  disabled={!draftName.trim() || createMut.isPending}
                  style={{
                    padding: '2px 8px',
                    background: 'var(--accent)',
                    color: 'white',
                    border: 'none',
                    borderRadius: 2,
                    fontSize: 'var(--t-meta)',
                    cursor: 'pointer',
                  }}
                >
                  Create
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setNaming(true)}
                style={menuItemStyle()}
                onMouseOver={(e) => (e.currentTarget.style.background = 'var(--hover-subtle)')}
                onMouseOut={(e) => (e.currentTarget.style.background = 'transparent')}
              >
                <Plus size={12} />
                <span>Create new ward…</span>
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Local copies of the two tiny style helpers (kept here so the button is
// self-contained and reusable without importing from the Company Overview panel).
export function actBtnStyle(on: boolean): React.CSSProperties {
  return {
    height: 22,
    border: `1px solid ${on ? 'var(--color-accent-300)' : 'var(--hairline)'}`,
    background: on ? 'var(--color-accent-100)' : 'var(--menu-surface)',
    color: on ? 'var(--accent)' : 'var(--text-secondary)',
    borderRadius: 4,
    fontSize: 'var(--t-meta)',
    fontWeight: 600,
    padding: '0 8px',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    cursor: 'pointer',
  };
}
function menuItemStyle(): React.CSSProperties {
  return {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--s-2)',
    width: '100%',
    padding: '4px 6px',
    background: 'transparent',
    border: 'none',
    borderRadius: 2,
    cursor: 'pointer',
    color: 'var(--text-primary)',
    font: 'inherit',
    textAlign: 'left',
  };
}
